import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {httpGetExports} from '@/features/exports/http/exports.http'
import {ExportJobListResponse, ExportsListParams} from '@/features/exports/types'
import {exportsListKey} from '@/features/exports/query/exportsKeys'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

const DEFAULT_LIMIT = 20

/**
 * Paginated list of the authenticated user's export jobs for the active filters.
 * The entity/status/date/archived filters are part of the query key, so changing a tab
 * or a filter triggers a fresh fetch and caches per-filter (FR-005..FR-007).
 */
export const useExports = (
    params: Omit<ExportsListParams, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<ExportJobListResponse>, 'enabled'>
) => {
    const limit = params.limit ?? DEFAULT_LIMIT

    const query = useInfiniteQuery({
        queryKey: exportsListKey(params),
        queryFn: ({pageParam = 1}) => httpGetExports({...params, page: pageParam, limit}),
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            const {currentPage, lastPage: lastPageNumber} = lastPage.meta
            return currentPage < lastPageNumber ? currentPage + 1 : null
        },
        ...options
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        jobs: query.data?.pages.flatMap(page => page.data) ?? [],
        total: query.data?.pages[0]?.meta.total ?? 0
    }
}
